import {Alert, Button, Col, Collapse, Drawer, Form, Icon, message, Modal, Row} from "antd";
import useManifestPrintout from "components/scheduling/Printout/useManifestPrintout";
import React, {useReducer} from "react";
import {Query, useApolloClient} from "react-apollo";
import moment from "moment";
import gql from "graphql-tag";
import {safeGet} from "common/util";
import CenteredLoadingScreen from "components/CenteredLoadingScreen";
import InputDollar from "components/form/input/input-dollar";
import { FINALIZED_FLIGHTS_FULL } from "components/flights/times/graphql";

const initialState = {
    drawerVisible: false,
    dataLoading: false,
    contracts: [],
    activeKeys: [],
    cachedFlights: null,
    cachedLogoURL: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'OPEN_DRAWER':
            return { ...initialState, drawerVisible: true, cachedLogoURL: action.logoURL, dataLoading: true };
        case 'DATA_LOADED':
            return {
                ...state,
                dataLoading: false,
                cachedFlights: action.flights,
                contracts: action.contracts,
                activeKeys: action.contracts.length > 0 ? [action.contracts[0]._id] : [],
            };
        case 'DATA_LOAD_ERROR':
            return { ...state, dataLoading: false };
        case 'SET_ACTIVE_KEYS':
            return { ...state, activeKeys: action.keys };
        case 'CLOSE_DRAWER':
            return { ...initialState };
        default:
            return state;
    }
}

export const PrintoutTimesButton = Form.create({ onValuesChange: () => {} })(({form, ...props}) => {
    const [fetchPrintout, fetching, printoutError] = useManifestPrintout('FlightTimes', 'FlightTimes');
    const [state, dispatch] = useReducer(reducer, initialState);
    const ApolloClient = useApolloClient();

    const { drawerVisible, dataLoading, contracts, activeKeys, cachedFlights, cachedLogoURL } = state;

    const userAttr = props.cognitoUser.attributes;
    const orgData = props.orgData;

    async function getFullFinalizedFlightsData(pageSizeWeeks = 2) {
        /** @type Moment */
        let startDate = props.dateRange[0];
        /** @type Moment */
        let endDate = props.dateRange[1];

        let duration = moment.duration(endDate.diff(startDate));
        let noWeeks = Math.ceil(duration.as('weeks'));

        function getDataPage(subStartDate, subEndDate) {
            return {
                execute: () => {
                    return ApolloClient.query({
                        variables: {
                            tpID: props.orgData.transporter._id,
                            customerID: props.orgData.customer._id,
                            startDate: subStartDate.format('YYYY-MM-DD'),
                            endDate: subEndDate.format('YYYY-MM-DD'),
                            departureID: props.departureID && props.departureID.key
                        },
                        query: FINALIZED_FLIGHTS_FULL,
                        fetchPolicy: 'no-cache'
                    })
                }
            };
        }

        // Fetch each page of data two pages at a time using two queues.
        const pageQueue = [];
        const results = [];
        let executeQueue = [];

        for (let pageNum = 0; pageNum <= noWeeks; pageNum += pageSizeWeeks) {
            let subStartDate = moment(startDate).add(pageNum, 'weeks');
            if (pageNum !== 0) {
                subStartDate.add(1, 'day'); // Prevent overlapping between pages
            }
            let subEndDateCandidate = moment(startDate)
                .add(pageNum + pageSizeWeeks, 'weeks');

            let subEndDate = subEndDateCandidate.isAfter(endDate, 'date') ? endDate : subEndDateCandidate;
            if (subStartDate.isAfter(subEndDate, 'date')) {
                continue;
            }
            const page = getDataPage(subStartDate, subEndDate);
            pageQueue.push(page);
        }

        while (pageQueue.length) {
            executeQueue.push(pageQueue.pop());
            if (executeQueue.length >= 3 || (!pageQueue.length && executeQueue.length)) {
                results.push(...await Promise.all(executeQueue.map(p => p.execute())));
                executeQueue = [];
            }
        }

        let compiledResults = [];
        results.forEach((queryResult, idx) => {
            if (queryResult.error) {
                console.error('Failed to fetch page ' + (idx + 1) + ' of results!', queryResult.error)
                return;
            }
            let data = queryResult.data.getFinalizedFlights.docs;
            compiledResults = compiledResults.concat(data);
        })
        return compiledResults;
    }

    const handleCloseDrawer = () => {
        form.resetFields();
        dispatch({ type: 'CLOSE_DRAWER' });
    };

    return (
        <Query
            query={gql`
                query GetCustomerManifestPrintout($id: ID!){
                    getCustomer(_id: $id){
                        _id
                        name
                        manifestPrintout {
                            type
                            layouts {
                                name
                                logo
                            }
                        }
                    }
                }
            `}
            variables={{id: orgData.customer._id}}
        >
            {({data}) => {
                const manifestPrintouts = safeGet(['getCustomer', 'manifestPrintout'], data) || [];
                const flightManifest = manifestPrintouts.find((m) => m.type === 'Flight');
                const hessLayout = flightManifest && flightManifest.layouts.find((l) => l.name === 'Hess');
                const logoURL = hessLayout && hessLayout.logo;

                const handleOpenDrawer = async () => {
                    form.resetFields();
                    dispatch({ type: 'OPEN_DRAWER', logoURL });

                    let flights;
                    try {
                        flights = await getFullFinalizedFlightsData(1);
                    } catch (err) {
                        dispatch({ type: 'DATA_LOAD_ERROR' });
                        console.error(err);
                        message.error('One or more pages of data failed to be fetched. You may need to narrow your date range.', 6);
                        return;
                    }

                    // Gather unique contracts from flights, sorted alphabetically
                    const contractMap = {};
                    flights.forEach(flight => {
                        if (flight.contract && flight.contract._id) {
                            contractMap[flight.contract._id] = flight.contract;
                        }
                    });
                    const sortedContracts = Object.values(contractMap).sort((a, b) =>
                        (a.name || '').localeCompare(b.name || '')
                    );

                    dispatch({ type: 'DATA_LOADED', flights, contracts: sortedContracts });
                };

                const handleSubmit = () => {
                    form.validateFieldsAndScroll((err, values) => {
                        if (err) {
                            const firstErrContract = contracts.find(c =>
                                Object.keys(err).some(k => k.startsWith(`contract_${c._id}_`))
                            );
                            if (firstErrContract) {
                                dispatch({
                                    type: 'SET_ACTIVE_KEYS',
                                    keys: activeKeys.includes(firstErrContract._id)
                                        ? activeKeys
                                        : [firstErrContract._id, ...activeKeys],
                                });
                            }
                            return;
                        }

                        const contractsWithZeros = contracts.filter(c => isContractHasZero(c._id));

                        const proceed = () => {
                            const contractCosts = [];
                            contracts.forEach(c => {
                                contractCosts.push({
                                    contractId: c._id,
                                    contractName: c.name,
                                    monthlyFixedCost: values[`contract_${c._id}_monthlyFixedCost`],
                                    hourlyFlightRateCost: values[`contract_${c._id}_hourlyFlightRateCost`],
                                    extraFuelCost: values[`contract_${c._id}_extraFuelCost`],
                                    parkingFeeCost: values[`contract_${c._id}_parkingFeeCost`],
                                });
                            });

                            const body = {
                                issuedBy: `${userAttr.given_name} ${userAttr.family_name}`,
                                orgName: orgData.customer.name,
                                logoURL: cachedLogoURL,
                                flights: cachedFlights,
                                layoutName: props.dateRange[0].format("MMMM DD, YYYY") === props.dateRange[1].format("MMMM DD, YYYY") ?
                                    props.dateRange[0].format("MMMM DD, YYYY") + ` ${orgData.customer.name}` :
                                    props.dateRange[0].format("MMMM DD, YYYY") + " - " + props.dateRange[1].format("MMMM DD, YYYY") + ` ${orgData.customer.name}`,
                                contractCosts,
                            };

                            console.debug('body', body);

                            fetchPrintout(body, true, `${orgData.customer.name} Flight Manifest ${props.dateRange[0].format('MMM DD, YYYY')} - ${props.dateRange[1].format('MMM DD, YYYY')}.xlsx`)
                                .catch((fetchErr) => {
                                    message.error('Failed to download printout', 3).then(() => {
                                        message.error(fetchErr.message, 5);
                                    }, null);
                                });
                        };

                        if (contractsWithZeros.length > 0) {
                            Modal.confirm({
                                title: 'Some contracts have zero values',
                                icon: <Icon type="exclamation-circle" style={{ color: '#faad14' }} />,
                                content: (
                                    <div>
                                        <p>The following contracts have one or more cost fields set to zero:</p>
                                        <ul>
                                            {contractsWithZeros.map(c => <li key={c._id}>{c.name || c._id}</li>)}
                                        </ul>
                                        <p>Do you want to continue anyway?</p>
                                    </div>
                                ),
                                okText: 'Continue',
                                cancelText: 'Cancel',
                                onOk: proceed,
                            });
                        } else {
                            proceed();
                        }
                    });
                };

                function renderLoading() {
                    if (!dataLoading) return null;
                    return <CenteredLoadingScreen
                        message={'Loading flights and contracts...'}
                    />;
                }

                function getContractFieldKeys(contractId) {
                    return [
                        `contract_${contractId}_monthlyFixedCost`,
                        `contract_${contractId}_hourlyFlightRateCost`,
                        `contract_${contractId}_extraFuelCost`,
                        `contract_${contractId}_parkingFeeCost`,
                    ];
                }

                function isContractAllFilled(contractId) {
                    return getContractFieldKeys(contractId).every(f => {
                        const val = form.getFieldValue(f);
                        return val !== undefined && val !== null && val !== '' && Number(val) > 0;
                    });
                }

                function isContractHasZero(contractId) {
                    return getContractFieldKeys(contractId).some(f => {
                        const val = form.getFieldValue(f);
                        return val === undefined || val === null || val === '' || Number(val) === 0;
                    });
                }

                function renderCostForms() {
                    if (dataLoading) return null;
                    if (contracts.length === 0) {
                        return <Alert type="warning" message="No contracts found in the fetched flights." showIcon/>;
                    }
                    return (
                        <Form layout="vertical">
                            <p style={{marginBottom: '1rem'}}>Enter costs for each contract used in this date range.</p>
                            <Collapse
                                activeKey={activeKeys}
                                onChange={keys => dispatch({ type: 'SET_ACTIVE_KEYS', keys })}
                                accordion
                            >
                                {contracts.map(contract => {
                                    const allFilled = isContractAllFilled(contract._id);
                                    const hasZero = isContractHasZero(contract._id);
                                    let panelExtra = null;
                                    if (allFilled) {
                                        panelExtra = <Icon type="check-circle" theme="filled" style={{color: '#52c41a'}} />;
                                    } else if (hasZero) {
                                        panelExtra = <Icon type="exclamation-circle" theme="filled" style={{color: '#faad14'}} />;
                                    }
                                    return (
                                        <Collapse.Panel
                                            key={contract._id}
                                            header={contract.name || contract._id}
                                            extra={panelExtra}
                                        >
                                            <Row gutter={16}>
                                                <Col xs={24} md={12}>
                                                    <Form.Item label="Monthly Fixed Cost">
                                                        {form.getFieldDecorator(`contract_${contract._id}_monthlyFixedCost`, {
                                                            initialValue: 0,
                                                        })(
                                                            <InputDollar min={0} style={{width: '100%'}}/>
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={12}>
                                                    <Form.Item label="Hourly Flight Rate">
                                                        {form.getFieldDecorator(`contract_${contract._id}_hourlyFlightRateCost`, {
                                                            initialValue: 0,
                                                        })(
                                                            <InputDollar min={0} style={{width: '100%'}}/>
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={12}>
                                                    <Form.Item label="Extra Fuel Cost">
                                                        {form.getFieldDecorator(`contract_${contract._id}_extraFuelCost`, {
                                                            initialValue: 0,
                                                        })(
                                                            <InputDollar min={0} style={{width: '100%'}}/>
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={12}>
                                                    <Form.Item label="Parking Fee">
                                                        {form.getFieldDecorator(`contract_${contract._id}_parkingFeeCost`, {
                                                            initialValue: 0,
                                                        })(
                                                            <InputDollar min={0} style={{width: '100%'}}/>
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </Collapse.Panel>
                                    );
                                })}
                            </Collapse>
                            <Form.Item style={{marginTop: '1rem'}}>
                                <Button
                                    type="primary"
                                    onClick={handleSubmit}
                                    loading={fetching}
                                    style={{width: '100%'}}
                                >
                                    Download
                                </Button>
                            </Form.Item>
                            <Form.Item>
                                <Button
                                    style={{width: '100%'}}
                                    onClick={handleCloseDrawer}
                                    disabled={fetching}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Form>
                    );
                }

                return (
                    <>
                        <Button onClick={handleOpenDrawer}>Download Times Printout <Icon type="right" /></Button>
                        <Drawer
                            title="Flight Times Printout"
                            width={600}
                            visible={drawerVisible}
                            onClose={handleCloseDrawer}
                            destroyOnClose
                        >
                            {renderLoading()}
                            {renderCostForms()}
                        </Drawer>
                    </>
                )
            }}
        </Query>
    )
})